import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DollarSign, Plus, X } from 'lucide-react';
import {
  BosDurum,
  Buton,
  Etiket,
  Girdi,
  TarihAraligiSecici,
  VeriTablo,
  tarihIso,
  toast,
  type ColumnDef,
  type TarihAraligi,
} from '@emanetx/ui';
import * as Dialog from '@radix-ui/react-dialog';
import {
  dovizKurlariniListele,
  dovizKuruEkle,
  type DovizKuru,
  type DovizKuruOlusturIstegi,
} from '@emanetx/sdk';
import { istemci } from '../istemci.js';

function son30Gun(): TarihAraligi {
  const e = new Date();
  const s = new Date();
  s.setDate(s.getDate() - 29);
  s.setHours(0, 0, 0, 0);
  return { baslangic: s, bitis: e };
}

const PARA_SEMBOL: Record<string, string> = { TRY: '₺', EUR: '€', USD: '$' };

export function SayfaDoviz() {
  const qc = useQueryClient();
  const [sayfa, setSayfa] = useState(1);
  const [aralik, setAralik] = useState<TarihAraligi>(son30Gun);
  const [dialogAcik, setDialogAcik] = useState(false);

  const sorgu = {
    sayfa,
    boyut: 100,
    ...(tarihIso(aralik.baslangic) && { baslangicTen: tarihIso(aralik.baslangic) }),
    ...(tarihIso(aralik.bitis) && { baslangicAKadar: tarihIso(aralik.bitis) }),
  };

  const liste = useQuery({
    queryKey: ['doviz', 'liste', sorgu],
    queryFn: () => dovizKurlariniListele(istemci, sorgu),
    placeholderData: (o) => o,
  });

  const ekle = useMutation({
    mutationFn: (g: DovizKuruOlusturIstegi) => dovizKuruEkle(istemci, g),
    onSuccess: () => {
      toast.success('Kur eklendi');
      void qc.invalidateQueries({ queryKey: ['doviz'] });
      setDialogAcik(false);
    },
    onError: (err: unknown) => {
      const m =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: string }).message)
          : 'Eklenemedi';
      toast.error(m);
    },
  });

  const kolonlar = useMemo<ColumnDef<DovizKuru, unknown>[]>(
    () => [
      {
        accessorKey: 'tarih',
        header: 'Tarih',
        meta: { etiket: 'Tarih' },
        cell: ({ row }) => (
          <span className="font-mono text-xs">
            {new Date(row.original.tarih).toLocaleDateString('tr-TR', {
              day: 'numeric',
              month: 'short',
              year: 'numeric',
            })}
          </span>
        ),
      },
      {
        id: 'parite',
        header: 'Parite',
        meta: { etiket: 'Parite' },
        enableSorting: false,
        cell: ({ row }) => (
          <span className="font-mono text-sm">
            {PARA_SEMBOL[row.original.kaynak]} {row.original.kaynak} →{' '}
            {PARA_SEMBOL[row.original.hedef]} {row.original.hedef}
          </span>
        ),
      },
      {
        accessorKey: 'kur',
        header: 'Kur',
        meta: { etiket: 'Kur' },
        cell: ({ row }) => (
          <span className="font-mono text-sm font-semibold">
            {Number(row.original.kur).toLocaleString('tr-TR', {
              minimumFractionDigits: 4,
              maximumFractionDigits: 8,
            })}
          </span>
        ),
        sortingFn: (a, b) => Number(a.original.kur) - Number(b.original.kur),
      },
      {
        accessorKey: 'saglayici',
        header: 'Sağlayıcı',
        meta: { etiket: 'Sağlayıcı' },
        cell: ({ row }) => (
          <span className="text-fume-500 text-xs uppercase tracking-wide">
            {row.original.saglayici}
          </span>
        ),
      },
      {
        accessorKey: 'olusturmaTarihi',
        header: 'Eklendi',
        meta: { etiket: 'Eklendi' },
        cell: ({ row }) => (
          <span className="text-fume-500 font-mono text-xs">
            {new Date(row.original.olusturmaTarihi).toLocaleString('tr-TR', {
              day: 'numeric',
              month: 'short',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Döviz Kurları</h1>
          <p className="text-fume-500 dark:text-fume-400 mt-1 text-sm">
            EUR/USD ↔ TRY ve diğer pariteler için günlük kur kayıtları.
          </p>
        </div>
        <Buton onClick={() => setDialogAcik(true)}>
          <Plus size={16} /> Manuel Kur Ekle
        </Buton>
      </header>

      <VeriTablo
        veri={liste.data?.veriler ?? []}
        kolonlar={kolonlar}
        toplam={liste.data?.toplam}
        sayfa={sayfa}
        boyut={100}
        onSayfa={setSayfa}
        yukleniyor={liste.isLoading}
        yenileniyor={liste.isFetching}
        onYenile={() => void liste.refetch()}
        bosDurum={
          <BosDurum
            ikon={DollarSign}
            baslik="Bu aralıkta kur kaydı yok"
            aciklama="Manuel kur ekleyebilir veya tarih aralığını genişletebilirsiniz."
          />
        }
        ustAksiyonlar={
          <TarihAraligiSecici
            deger={aralik}
            onDegisim={(a) => {
              setAralik(a);
              setSayfa(1);
            }}
            etiketYok
            className="h-10 text-xs"
          />
        }
      />

      <KurEkleDialog
        acik={dialogAcik}
        onDegisim={setDialogAcik}
        onGonder={(g) => ekle.mutate(g)}
        calisiyor={ekle.isPending}
      />
    </div>
  );
}

interface DialogOzellikleri {
  acik: boolean;
  onDegisim: (a: boolean) => void;
  onGonder: (g: DovizKuruOlusturIstegi) => void;
  calisiyor: boolean;
}

function KurEkleDialog({ acik, onDegisim, onGonder, calisiyor }: DialogOzellikleri) {
  const [kaynak, setKaynak] = useState<'TRY' | 'EUR' | 'USD'>('EUR');
  const [hedef, setHedef] = useState<'TRY' | 'EUR' | 'USD'>('TRY');
  const [kur, setKur] = useState('');
  const [tarih, setTarih] = useState(() => new Date().toISOString().slice(0, 10));
  const [saglayici, setSaglayici] = useState('manuel');

  const formGecerli =
    kaynak !== hedef && /^\d+(\.\d{1,8})?$/.test(kur) && tarih && saglayici.length >= 2;

  return (
    <Dialog.Root open={acik} onOpenChange={onDegisim}>
      <Dialog.Portal>
        <Dialog.Overlay className="bg-fume-900/50 fixed inset-0 z-40 backdrop-blur-sm" />
        <Dialog.Content className="dark:bg-fume-900 fixed left-1/2 top-1/2 z-50 w-[92vw] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white p-6 shadow-2xl">
          <div className="mb-4 flex items-center justify-between">
            <Dialog.Title className="text-lg font-semibold">Manuel Kur Ekle</Dialog.Title>
            <Dialog.Close asChild>
              <button aria-label="Kapat" className="text-fume-400 hover:text-fume-700">
                <X size={18} />
              </button>
            </Dialog.Close>
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!formGecerli || calisiyor) return;
              const d = new Date(tarih);
              d.setUTCHours(0, 0, 0, 0);
              onGonder({ kaynak, hedef, kur, tarih: d.toISOString(), saglayici });
            }}
            className="space-y-3"
          >
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Etiket htmlFor="k">Kaynak</Etiket>
                <select
                  id="k"
                  value={kaynak}
                  onChange={(e) => setKaynak(e.target.value as 'TRY' | 'EUR' | 'USD')}
                  className="border-fume-200 dark:border-fume-700 dark:bg-fume-900 dark:text-fume-50 mt-1 h-11 w-full rounded-lg border bg-white px-3 text-sm"
                >
                  <option value="TRY">TRY ₺</option>
                  <option value="EUR">EUR €</option>
                  <option value="USD">USD $</option>
                </select>
              </div>
              <div>
                <Etiket htmlFor="h">Hedef</Etiket>
                <select
                  id="h"
                  value={hedef}
                  onChange={(e) => setHedef(e.target.value as 'TRY' | 'EUR' | 'USD')}
                  className="border-fume-200 dark:border-fume-700 dark:bg-fume-900 dark:text-fume-50 mt-1 h-11 w-full rounded-lg border bg-white px-3 text-sm"
                >
                  <option value="TRY">TRY ₺</option>
                  <option value="EUR">EUR €</option>
                  <option value="USD">USD $</option>
                </select>
              </div>
            </div>
            <div>
              <Etiket htmlFor="kur">Kur</Etiket>
              <Girdi
                id="kur"
                value={kur}
                onChange={(e) => setKur(e.target.value)}
                placeholder="35.4250"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Etiket htmlFor="t">Tarih</Etiket>
                <Girdi
                  id="t"
                  type="date"
                  value={tarih}
                  onChange={(e) => setTarih(e.target.value)}
                  required
                />
              </div>
              <div>
                <Etiket htmlFor="sa">Sağlayıcı</Etiket>
                <Girdi
                  id="sa"
                  value={saglayici}
                  onChange={(e) => setSaglayici(e.target.value)}
                  placeholder="manuel / tcmb / ecb"
                />
              </div>
            </div>
            <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
              <Dialog.Close asChild>
                <Buton stil="cizgili" boyut="orta" disabled={calisiyor}>
                  İptal
                </Buton>
              </Dialog.Close>
              <Buton type="submit" disabled={!formGecerli || calisiyor}>
                {calisiyor ? 'Ekleniyor...' : 'Kur Ekle'}
              </Buton>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
